import { useState, useEffect } from "react";
import PageWrapper from "../../containers/PageWrapper";
import { PERSISTED_FIRM_REVIEW_FILTERS } from "../../__constants__";
import FirmProfileHead from "../FirmProfile/FirmProfileHead";
import AdviserCarousel from "./components/AdviserCarousel";
import Hero from "./components/Hero";
import About, { ABOUT_SHOW_MORE_LIMIT } from "./components/About";
import Services, {
  SERVICES_SHOW_MORE_LIMIT,
} from "./components/Services/Services";
import Reviews from "./components/Reviews/Reviews";
import FirmDetails from "./components/FirmDetails";
import MobileMenu from "./components/MobileMenu";
import { track } from "../../analytics";
import ComponentWrapper from "./components/ComponentWrapper";
import Claim from "./components/Claim";
import ButtonSVG from "../Inputs/ButtonSVG";
import { get } from "lodash";
import useWindowWidth from "../../hooks/useWindowWidth";

const FirmPage = (props) => {
  const {
    profile,
    firmOrNetworkType,
    services,
    initialReview,
    advisers,
    awards,
    accreditations,
    reviewFilters,
    unclaimed,
    initialPage,
  } = props;
  const windowWidth = useWindowWidth()
  const isXs = windowWidth <= 600;
  const isFirm = firmOrNetworkType === "firm";
  const [expand, setExpand] = useState(false);
  const [reviewFiltersQueryString] = useState(
    () => {
      if (process.browser) {
        const reviewFilters = sessionStorage.getItem(
          PERSISTED_FIRM_REVIEW_FILTERS
        );
        return reviewFilters ? JSON.parse(reviewFilters).query : "";
      } else {
        return "";
      }
    }
  );
  const verifiedCount = get(profile, ["statistics", "verified_advisers"], 0);

  const [claimProfileUrl, setClaimProfileUrl] = useState("");

  useEffect(() => {
    const cachedParams = JSON.parse(sessionStorage.getItem("utmParams"));
    const searchParams = new URLSearchParams(window.location.search);

    const utmParams = {
      utm_source: searchParams.get("utm_source") || cachedParams?.utm_source,
      utm_medium: searchParams.get("utm_medium") || cachedParams?.utm_medium,
      utm_content: searchParams.get("utm_content") || cachedParams?.utm_content,
      contactId: searchParams.get("contactId") || cachedParams?.contactId,
    };

    sessionStorage.setItem("utmParams", JSON.stringify(utmParams));

    const baseUrl = "https://insights.vouchedfor.co.uk/claim-your-profile/";
    const urlParams = new URLSearchParams();

    Object.entries(utmParams).forEach(([key, value]) => {
      if (value) urlParams.append(key, value);
    });

    setClaimProfileUrl(`${baseUrl}?${urlParams}`);
  }, []);

  const handleExpandClick = () => {
    track(
      `${expand ? "Closed" : "Opened"} about and services expand button`,
      {
        category:
          firmOrNetworkType === "firm" ? "Firm Profile" : "Network Profile",
      }
    );
    setExpand(!expand);
  };

  const renderMobile = () => (
    <>
      <AdviserCarousel
        firmOrNetworkType={firmOrNetworkType}
        advisers={advisers?.data}
        profile={profile}
        total={advisers?.aggregate_data?.total}
        firmId={props.firmId}
        claimProfileUrl={claimProfileUrl}
        unclaimed={unclaimed}
      />
      <Reviews
        initialReview={initialReview}
        profile={profile}
        reviewFilters={reviewFilters}
        firmOrNetworkType={firmOrNetworkType}
        initialPage={initialPage}
        firmId={props.firmId}
        unclaimed={unclaimed}
      />
      <About
        about={profile.about}
        background
        unclaimed={unclaimed}
        name={profile.name}
        firmOrNetworkType={firmOrNetworkType}
        useWrapper
        firmId={props.firmId}
        claimProfileUrl={claimProfileUrl}
      />
      <FirmDetails
        render={isFirm}
        unclaimed={unclaimed}
        awards={awards}
        accreditations={accreditations}
        location={profile.firm_address}
        firmOrNetworkType={firmOrNetworkType}
        firmId={props.firmId}
        claimProfileUrl={claimProfileUrl}
        name={profile.name}
      />
      <ComponentWrapper render={isFirm && !unclaimed && verifiedCount}>
        <Services
          claimProfileUrl={claimProfileUrl}
          services={services?.length ? services : []}
          unclaimed={unclaimed}
          name={profile.name}
          firmId={props.firmId}
        />
      </ComponentWrapper>
    </>
  );

  const renderDesktop = () => (
    <>
      <ComponentWrapper render={isFirm && !unclaimed && verifiedCount}>
        <div className="w-full flex flex-wrap box-border">
          <div className="grow-0 basis-2/3 m-0 box-border">
            <About
              about={profile.about}
              unclaimed={unclaimed}
              name={profile.name}
              split={true}
              expand={expand}
              firmOrNetworkType={firmOrNetworkType}
              firmId={props.firmId}
              claimProfileUrl={claimProfileUrl}
            />
          </div>
          <div className="grow-0 basis-1/3 m-0 box-border">
            <Services
              split={true}
              expand={expand}
              services={services?.length ? services : []}
              unclaimed={unclaimed}
              name={profile.name}
              firmId={props.firmId}
              claimProfileUrl={claimProfileUrl}
            />
          </div>
          {profile.about?.length > ABOUT_SHOW_MORE_LIMIT ||
          services?.length > SERVICES_SHOW_MORE_LIMIT ? (
            <div className="m-0 box-border grow-0 w-full">
              <ButtonSVG
                expand={expand}
                onClick={handleExpandClick}
                dataTest="about-and-services-expand-icon"
              />
            </div>
          ) : null}
        </div>
      </ComponentWrapper>
      <ComponentWrapper render={!isFirm || unclaimed || !verifiedCount}>
        <About
          about={profile.about}
          unclaimed={unclaimed}
          name={profile.name}
          firmOrNetworkType={firmOrNetworkType}
          firmId={props.firmId}
          claimProfileUrl={claimProfileUrl}
        />
      </ComponentWrapper>

      <AdviserCarousel
        firmOrNetworkType={firmOrNetworkType}
        advisers={advisers?.data}
        profile={profile}
        total={advisers?.aggregate_data?.total}
        unclaimed={unclaimed}
        firmId={props.firmId}
        claimProfileUrl={claimProfileUrl}
      />
      <FirmDetails
        render={isFirm}
        unclaimed={unclaimed}
        awards={awards}
        accreditations={accreditations}
        location={profile.firm_address}
        firmOrNetworkType={firmOrNetworkType}
        name={profile.name}
        firmId={props.firmId}
        claimProfileUrl={claimProfileUrl}
      />
      <Reviews
        initialReview={initialReview}
        profile={profile}
        reviewFilters={reviewFilters}
        firmOrNetworkType={firmOrNetworkType}
        initialPage={initialPage}
        firmId={props.firmId}
        unclaimed={unclaimed}
      />
    </>
  );

  return (
    <>
      <FirmProfileHead
        profile={profile}
        firmOrNetworkType={firmOrNetworkType}
        services={services}
        disableBots={reviewFiltersQueryString !== ""}
      />
      <PageWrapper noPadding pathname={`/${firmOrNetworkType}`}>
        <Hero
          profile={profile}
          firmOrNetworkType={firmOrNetworkType}
          unclaimed={unclaimed}
          claimProfileUrl={claimProfileUrl}
          firmId={props.firmId}
        />
        <MobileMenu
          render={isXs}
          firmOrNetworkType={firmOrNetworkType}
          unclaimed={unclaimed}
          adviserCount={advisers?.aggregate_data?.total}
          verifiedAdviserCount={verifiedCount}
        />
        <Claim
          render={unclaimed && isFirm}
          name={profile.name}
          firmId={props.firmId}
          claimProfileUrl={claimProfileUrl}
        />

        {isXs ? renderMobile() : renderDesktop()}
      </PageWrapper>
    </>
  );
};

export default FirmPage;
